import React, { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import ScissorsMascot from '../components/ScissorsMascot';
import { toast } from 'sonner';
import { Loader2, Globe, CheckCircle } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const Register = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [lookAt, setLookAt] = useState({ x: 0, y: 0 });
  const [isPeeking, setIsPeeking] = useState(false);
  const [validationMsg, setValidationMsg] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [displayCode, setDisplayCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  
  const { register } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  if (showVerification) {
      return <Navigate to="/verify-email" replace />;
  }

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    const offsetX = Math.min(Math.max((val.length / 30) * 2 - 1, -1), 1);
    setLookAt({ x: offsetX, y: 1 });
  };

  const handleUsernameChange = (e) => {
    const val = e.target.value;
    setUsername(val);
    const offsetX = Math.min(Math.max((val.length / 30) * 2 - 1, -1), 1);
    setLookAt({ x: offsetX, y: 1 });
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    
    // Check for complex password requirements
    const hasUppercase = /[A-Z]/.test(val);
    const hasNumber = /[0-9]/.test(val);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(val);
    const isLongEnough = val.length >= 8;

    if (val) {
      if (!isLongEnough) {
        setIsPeeking(true);
        setValidationMsg(language === 'de' ? 'Mindestens 8 Zeichen benötigt' : 'At least 8 characters required');
      } else if (!hasUppercase) {
        setIsPeeking(true);
        setValidationMsg(language === 'de' ? 'Bitte verwende Großbuchstaben' : 'Please use uppercase letters');
      } else if (!hasNumber) {
        setIsPeeking(true);
        setValidationMsg(language === 'de' ? 'Bitte verwende Zahlen' : 'Please use numbers');
      } else if (!hasSpecial) {
        setIsPeeking(true);
        setValidationMsg(language === 'de' ? 'Bitte verwende Sonderzeichen' : 'Please use special characters');
      } else {
        setIsPeeking(false);
        setValidationMsg('');
      }
    } else {
      setIsPeeking(false);
      setValidationMsg('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('passwordsNoMatch'));
      return;
    }

    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    if (password.length < 8 || !hasUppercase || !hasNumber || !hasSpecial) {
      toast.error(language === 'de' 
        ? 'Passwort erfüllt nicht die Anforderungen (8 Zeichen, Großbuchstabe, Zahl, Sonderzeichen)' 
        : 'Password does not meet requirements (8 chars, uppercase, number, special char)'
      );
      return;
    }

    setLoading(true);

    try {
      // PayPal wird erst beim Beitritt zu einer PayPal-Organisation verlangt.
      await register(email, username, password, null);
      toast.success(language === 'de' 
        ? 'Account erstellt! Bitte verifiziere jetzt deine E-Mail.' 
        : 'Account created! Please verify your email now.'
      );
      navigate('/verify-email');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifyLoading(true);
    try {
      await verifyEmail(verificationCode);
      toast.success(language === 'de' ? 'E-Mail verifiziert!' : 'Email verified!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      const result = await resendVerification();
      setDisplayCode(result.verification_code);
      toast.success(language === 'de' 
        ? `Neuer Code: ${result.verification_code}` 
        : `New code: ${result.verification_code}`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  return (
    <div className="auth-container" data-testid="register-page">
      <LanguageSelector
        className="absolute top-[calc(1rem+env(safe-area-inset-top))] right-4 z-10 border border-border bg-background/50 backdrop-blur-md rounded-xl"
        data-testid="register-language-toggle"
      />

      {!showVerification ? (
        <Card className="auth-card shadow-lg">
          <CardHeader className="text-center pb-2 sm:pb-6">
            <div className="flex justify-center mb-2 sm:mb-4">
              <ScissorsMascot 
                size="md" 
                lookAt={lookAt.x} 
                lookAtY={lookAt.y}
                isPassword={isPasswordFocused} 
                isPeeking={isPeeking}
                validationMessage={validationMsg}
              />
            </div>
            <CardTitle className="text-2xl sm:text-3xl font-extrabold tracking-tight">{t('createAccount')}</CardTitle>
            <CardDescription className="text-sm sm:text-base">{t('joinScherenmarket')}</CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs sm:text-sm font-semibold ml-1">{language === 'de' ? 'E-Mail' : 'Email'}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="trader@example.com"
                    value={email}
                    onChange={handleEmailChange}
                    onFocus={() => {
                      setIsPasswordFocused(false);
                      setIsPeeking(false);
                      setValidationMsg('');
                    }}
                    required
                    className="h-12 text-base rounded-xl"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username" className="text-xs sm:text-sm font-semibold ml-1">{language === 'de' ? 'Benutzername' : 'Username'}</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="traderpro"
                    value={username}
                    onChange={handleUsernameChange}
                    onFocus={() => {
                      setIsPasswordFocused(false);
                      setIsPeeking(false);
                      setValidationMsg('');
                    }}
                    minLength={3}
                    maxLength={30}
                    required
                    className="h-12 text-base rounded-xl"
                    data-testid="register-username-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs sm:text-sm font-semibold ml-1">{t('password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={handlePasswordChange}
                    onFocus={() => {
                      setIsPasswordFocused(true);
                      if (password && !/[A-Z]/.test(password)) {
                        setIsPeeking(true);
                        setValidationMsg(language === 'de' ? 'Bitte verwende Großbuchstaben' : 'Please use uppercase letters');
                      }
                    }}
                    onBlur={() => {
                      setIsPasswordFocused(false);
                      setIsPeeking(false);
                      setValidationMsg('');
                    }}
                    minLength={6}
                    required
                    className="h-12 text-base rounded-xl"
                    data-testid="register-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-xs sm:text-sm font-semibold ml-1">{t('confirmPassword')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => {
                      setIsPasswordFocused(true);
                      setIsPeeking(false);
                      setValidationMsg('');
                    }}
                    onBlur={() => {
                      setIsPasswordFocused(false);
                    }}
                    required
                    className="h-12 text-base rounded-xl"
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

            </CardContent>

            <CardFooter className="flex flex-col gap-6 pt-2">
              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700 h-12 text-base font-bold rounded-xl shadow-lg shadow-red-600/20 active:scale-[0.98] transition-all"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('creatingAccount')}
                  </>
                ) : (
                  t('createAccount')
                )}
              </Button>

              <div className="flex flex-col items-center gap-4">
                <p className="text-sm text-muted-foreground text-center">
                  {t('hasAccount')}{' '}
                  <Link to="/login" className="text-red-500 hover:text-red-400 font-bold transition-colors" data-testid="login-link">
                    {t('signIn')}
                  </Link>
                </p>
                <div className="flex gap-6 text-[11px] font-medium text-muted-foreground/50 uppercase tracking-wider">
                  <Link to="/impressum" className="hover:text-foreground transition-colors">Impressum</Link>
                  <Link to="/datenschutz" className="hover:text-foreground transition-colors">{language === 'de' ? 'Datenschutz' : 'Privacy'}</Link>
                </div>
              </div>
            </CardFooter>
          </form>
        </Card>
      ) : (
        <Card className="auth-card shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-green-500 animate-in zoom-in duration-300" />
            </div>
            <CardTitle className="text-2xl font-bold">
              {language === 'de' ? 'E-Mail verifizieren' : 'Verify Email'}
            </CardTitle>
            <CardDescription className="text-sm">
              {language === 'de' 
                ? 'Gib den Verifizierungscode ein'
                : 'Enter the verification code'
              }
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleVerify}>
            <CardContent className="space-y-6 pt-4">
              {displayCode && (
                <div className="p-4 rounded-xl bg-green-600/10 border border-green-600/20 text-center animate-in fade-in slide-in-from-top-2 duration-500">
                  <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider font-bold">
                    {language === 'de' ? 'Dein Code' : 'Your code'}
                  </p>
                  <p className="text-4xl font-mono font-black text-green-500 tracking-[0.2em] ml-[0.2em]">{displayCode}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs font-semibold ml-1 uppercase tracking-wider text-muted-foreground/70">{language === 'de' ? 'Verifizierungscode' : 'Verification Code'}</Label>
                <Input
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  className="h-16 text-center text-3xl font-mono font-bold tracking-[0.5em] rounded-xl border-2 focus:border-green-500/50"
                  required
                />
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-4 pt-2">
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 h-12 rounded-xl font-bold text-base shadow-lg shadow-green-600/20" disabled={verifyLoading}>
                {verifyLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {language === 'de' ? 'Verifizieren' : 'Verify'}
              </Button>
              
              <div className="flex gap-2 w-full">
                <Button type="button" variant="outline" className="flex-1 h-11 rounded-xl" onClick={handleResend}>
                  {language === 'de' ? 'Neuer Code' : 'Resend'}
                </Button>
                <Button type="button" variant="ghost" className="flex-1 h-11 rounded-xl text-muted-foreground" onClick={handleSkip}>
                  {language === 'de' ? 'Später' : 'Skip for now'}
                </Button>
              </div>
            </CardFooter>
          </form>
        </Card>
      )}
    </div>
  );
};

export default Register;
